import React, { useState, useEffect } from 'react';
import { Percent, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GSTSettings() {
  const [settings, setSettings] = useState({ enabled: false, reg_no: '', rate: 9, effective_date: '' });
  const [paynow, setPaynow] = useState({ enabled: true, proxy_type: 'UEN', proxy_value: '', company_name: 'NJK Logistics Pte Ltd' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPn, setSavingPn] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/settings/gst`),
      axios.get(`${API}/api/settings/paynow`),
    ])
      .then(([g, p]) => { setSettings(g.data); setPaynow(p.data); })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSavePaynow = async () => {
    if (paynow.enabled && !(paynow.proxy_value || '').trim()) {
      toast.error('Please enter your PayNow UEN or mobile number');
      return;
    }
    setSavingPn(true);
    try {
      await axios.put(`${API}/api/settings/paynow`, paynow);
      toast.success(paynow.enabled
        ? 'PayNow QR enabled — invoices now show a scan-to-pay QR with amount and reference pre-filled'
        : 'PayNow QR disabled on invoices', { duration: 8000 });
    } catch (e) {
      toast.error('Failed to save PayNow settings');
    } finally {
      setSavingPn(false);
    }
  };

  const handleSave = async () => {
    if (settings.enabled && !settings.reg_no.trim()) {
      toast.error('Please enter your GST registration number');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/gst`, { ...settings, rate: parseFloat(settings.rate) || 0 });
      toast.success(settings.enabled
        ? `GST enabled — new invoices will show "TAX INVOICE" with ${settings.rate}% GST`
        : 'GST disabled — invoices stay as normal invoices with 0% GST', { duration: 8000 });
    } catch (e) {
      toast.error('Failed to save GST settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40" data-testid="gst-settings-page">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Percent className="text-[#5c1a1a]" size={28} /> GST & Payment Settings
          </h1>
          <p className="text-gray-600 mt-1">Once you register for GST, enable it here — new invoices will automatically become "TAX INVOICE" with GST added on top.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">GST Registration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="flex items-center justify-between bg-gray-50 border rounded-lg p-4">
                  <div>
                    <p className="font-semibold text-gray-900">Charge GST on invoices</p>
                    <p className="text-sm text-gray-500">Applies to invoices created after you enable this. Existing invoices are not changed.</p>
                  </div>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                    data-testid="gst-enabled-switch"
                  />
                </div>

                <div>
                  <Label>GST Registration Number</Label>
                  <Input
                    value={settings.reg_no || ''}
                    onChange={(e) => setSettings({ ...settings, reg_no: e.target.value })}
                    placeholder="e.g. 202412345M or M90312345A"
                    data-testid="gst-regno-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shown on every tax invoice header.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>GST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={settings.rate}
                      onChange={(e) => setSettings({ ...settings, rate: e.target.value })}
                      data-testid="gst-rate-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Singapore standard rate: 9%</p>
                  </div>
                  <div>
                    <Label>Effective Date</Label>
                    <Input
                      type="date"
                      value={settings.effective_date || ''}
                      onChange={(e) => setSettings({ ...settings, effective_date: e.target.value })}
                      data-testid="gst-effective-date-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Your IRAS registration effective date (for reference)</p>
                  </div>
                </div>

                <Button onClick={handleSave} disabled={saving} className="bg-[#5c1a1a] hover:bg-[#7a2525] w-full" data-testid="gst-save-btn">
                  <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save GST Settings'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">PayNow QR on Invoices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between bg-gray-50 border rounded-lg p-4">
              <div>
                <p className="font-semibold text-gray-900">Show scan-to-pay PayNow QR</p>
                <p className="text-sm text-gray-500">Every invoice (screen + PDF) gets a QR with the balance due and invoice number pre-filled — customers just scan and pay.</p>
              </div>
              <Switch
                checked={paynow.enabled}
                onCheckedChange={(v) => setPaynow({ ...paynow, enabled: v })}
                data-testid="paynow-enabled-switch"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PayNow Type</Label>
                <select
                  value={paynow.proxy_type}
                  onChange={(e) => setPaynow({ ...paynow, proxy_type: e.target.value })}
                  className="w-full h-10 border rounded-md px-3 bg-white text-sm"
                  data-testid="paynow-type-select"
                >
                  <option value="UEN">UEN (Company)</option>
                  <option value="MOBILE">Mobile Number</option>
                </select>
              </div>
              <div>
                <Label>{paynow.proxy_type === 'UEN' ? 'UEN' : 'Mobile (+65...)'}</Label>
                <Input
                  value={paynow.proxy_value || ''}
                  onChange={(e) => setPaynow({ ...paynow, proxy_value: e.target.value })}
                  placeholder={paynow.proxy_type === 'UEN' ? 'e.g. 202606027K' : '+6598366186'}
                  data-testid="paynow-value-input"
                />
              </div>
            </div>
            <div>
              <Label>Display Name</Label>
              <Input
                value={paynow.company_name || ''}
                onChange={(e) => setPaynow({ ...paynow, company_name: e.target.value })}
                data-testid="paynow-name-input"
              />
            </div>
            <Button onClick={handleSavePaynow} disabled={savingPn} className="bg-[#5c1a1a] hover:bg-[#7a2525] w-full" data-testid="paynow-save-btn">
              <Save size={16} className="mr-2" /> {savingPn ? 'Saving...' : 'Save PayNow Settings'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
